import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const VersionHistoryPanel = ({ versions, onRestore, onCompare }) => {
  const [selectedVersions, setSelectedVersions] = useState([]);

  const handleVersionSelect = (versionId) => {
    if (selectedVersions?.includes(versionId)) {
      setSelectedVersions(selectedVersions?.filter(id => id !== versionId));
    } else if (selectedVersions?.length < 2) {
      setSelectedVersions([...selectedVersions, versionId]);
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'created':
        return 'text-success bg-success/10';
      case 'updated':
        return 'text-primary bg-primary/10';
      case 'deleted':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'created':
        return 'Plus';
      case 'updated':
        return 'Edit';
      case 'deleted':
        return 'Trash2';
      default:
        return 'FileText';
    }
  };

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="History" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Versionshistorie</h3>
        </div>
        {selectedVersions?.length === 2 && (
          <Button
            variant="outline"
            size="sm"
            iconName="GitCompare"
            iconPosition="left"
            onClick={() => onCompare(selectedVersions)}
          >
            Versionen vergleichen
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {versions?.map((version, index) => (
          <div
            key={version?.id}
            className={`p-4 rounded-lg border transition-all ${
              selectedVersions?.includes(version?.id)
                ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/50'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedVersions?.includes(version?.id)}
                onChange={() => handleVersionSelect(version?.id)}
                className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
                disabled={selectedVersions?.length >= 2 && !selectedVersions?.includes(version?.id)}
              />

              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      Version {versions?.length - index}
                    </span>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs rounded-full">
                        Aktuell
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {version?.timestamp}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{version?.modifiedBy}</p>
                    <p className="text-xs text-muted-foreground">{version?.role}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {version?.changes?.map((change, changeIndex) => (
                    <div
                      key={changeIndex}
                      className="flex items-start gap-2 text-sm"
                    >
                      <div className={`px-2 py-1 rounded flex items-center gap-1 ${getChangeTypeColor(change?.type)}`}>
                        <Icon name={getChangeTypeIcon(change?.type)} size={12} />
                        <span className="text-xs font-medium capitalize">{change?.type}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-foreground">
                          <span className="font-medium">{change?.field}:</span>{' '}
                          {change?.oldValue && (
                            <span className="line-through text-muted-foreground">
                              {change?.oldValue}
                            </span>
                          )}
                          {change?.oldValue && change?.newValue && ' → '}
                          {change?.newValue && (
                            <span className="text-foreground">{change?.newValue}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {version?.comment && (
                  <div className="mt-3 p-2 bg-muted/50 rounded text-sm text-muted-foreground">
                    <Icon name="MessageSquare" size={14} className="inline mr-1" />
                    {version?.comment}
                  </div>
                )}

                {index !== 0 && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={() => onRestore(version?.id)}
                    >
                      Wiederherstellen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {versions?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Keine Versionshistorie verfügbar
          </p>
        </div>
      )}
      <div className="mt-4 flex items-start gap-2 p-3 bg-accent/5 rounded-lg border border-accent/20">
        <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Alle Änderungen werden automatisch protokolliert und können bis zu 90 Tage rückgängig gemacht werden. 
          Wählen Sie zwei Versionen aus, um Unterschiede zu vergleichen.
        </p>
      </div>
    </div>
  );
};

export default VersionHistoryPanel;